import React, { useState, useEffect } from 'react';
import { Package, Plus, Trash2, X, CheckSquare } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getAll as getAllGarmentTypes } from '@/pages/parametrizacion/garmentTypes/Services/garmentTypes.services';

const ModalEntregaRopa = ({ isOpen, onClose, empleado, onRegistrar }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [tiposPrenda, setTiposPrenda] = useState([]);
  const [prendas, setPrendas] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadTiposPrenda();
      
      // Limpiar prendas al abrir
      setPrendas([]);
    }
  }, [isOpen]);

  const loadTiposPrenda = async () => {
    try {
      const response = await getAllGarmentTypes();
      const tipos = response?.data || [];
      
      setTiposPrenda(tipos);
      
      if (tipos.length === 0) {
        toast({
          title: 'Aviso',
          description: 'No hay tipos de prenda configurados',
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error cargando tipos de prenda:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los tipos de prenda',
        variant: 'destructive'
      });
    }
  };

  const handleTogglePrendaSeleccionada = (tipoPrenda) => {
    const existingPrenda = prendas.find(p => p.idPrenda === tipoPrenda._id);
    
    if (existingPrenda) {
      // Deseleccionar: remover de la lista
      setPrendas(prendas.filter(p => p.idPrenda !== tipoPrenda._id));
    } else {
      // Seleccionar y agregar directamente a la lista
      const nuevaPrenda = {
        id: Date.now() + Math.random(),
        idPrenda: tipoPrenda._id,
        nombre: tipoPrenda.nombre,
        descripcion: tipoPrenda.descripcion || '',
        cantidad: 1,
        observaciones: ''
      };
      setPrendas([...prendas, nuevaPrenda]);
    }
  };

  const handleCantidadChange = (tipoPrendaId, nuevaCantidad) => {
    const cantidad = parseInt(nuevaCantidad) || 1;
    setPrendas(prendas.map(p => 
      p.idPrenda === tipoPrendaId ? { ...p, cantidad } : p
    ));
  };


  const handleEliminarPrenda = (id) => {
    setPrendas(prendas.filter(p => p.id !== id));
  };

  const handleRegistrar = async () => {
    if (prendas.length === 0) {
      toast({
        title: 'Error',
        description: 'Debe agregar al menos una prenda',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await onRegistrar(prendas);
      onClose();
    } catch (error) {
      console.error('Error registrando entrega:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[80vw] h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-purple-500" />
            Registrar Prendas - {empleado?.nombre + ' ' + empleado?.apellido}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-y-auto">
          {/* Selección de Prendas */}
          <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold flex items-center gap-2">
                <Package className="h-4 w-4 text-green-500" />
                Seleccionar Prendas
              </h4>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const prendasIds = prendas.map(p => p.idPrenda);
                    const faltantes = tiposPrenda.filter(tipo => !prendasIds.includes(tipo._id));
                    
                    if (faltantes.length === 0) {
                      // Limpiar todas
                      setPrendas([]);
                      toast({
                        title: 'Prendas eliminadas',
                        description: 'Se eliminaron todas las prendas seleccionadas'
                      });
                    } else {
                      // Agregar todas las faltantes
                      const nuevasPrendas = faltantes.map(tipo => ({
                        id: Date.now() + Math.random(),
                        idPrenda: tipo._id,
                        nombre: tipo.nombre,
                        descripcion: tipo.descripcion || '',
                        cantidad: 1,
                        observaciones: ''
                      }));
                      setPrendas([...prendas, ...nuevasPrendas]);
                      toast({
                        title: 'Prendas agregadas',
                        description: `${nuevasPrendas.length} prenda(s) agregada(s)`
                      });
                    }
                  }}
                  className="h-8 text-xs"
                >
                  <CheckSquare className="h-3 w-3 mr-1" />
                  {prendas.length === tiposPrenda.length ? 'Limpiar todo' : 'Agregar todos'}
                </Button>
                <span className="text-sm text-muted-foreground">
                  {prendas.length} seleccionada(s)
                </span>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-80 overflow-y-auto p-1">
              {tiposPrenda.map((tipo) => {
                const existingPrenda = prendas.find(p => p.idPrenda === tipo._id);
                const isSelected = !!existingPrenda;
                
                return (
                  <div
                    key={tipo._id}
                    className={`border rounded-lg p-4 transition-all cursor-pointer hover:shadow-md ${
                      isSelected 
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                        : 'border-gray-200 hover:border-gray-300 bg-white dark:bg-gray-800'
                    }`}
                    onClick={() => handleTogglePrendaSeleccionada(tipo)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="mt-1">
                          <div className={`h-4 w-4 rounded border-2 flex items-center justify-center ${
                            isSelected 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                        </div>
                        <div className="flex-1">
                          <div>
                            <p className="font-medium text-sm">{tipo.nombre}</p>
                            {tipo.descripcion && (
                              <p className="text-xs text-muted-foreground mt-1">{tipo.descripcion}</p>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      {isSelected && (
                        <div className="flex items-center gap-1 ml-2">
                          <Label className="text-xs whitespace-nowrap">Cant:</Label>
                          <Input
                            type="number"
                            min="1"
                            value={existingPrenda?.cantidad || 1}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleCantidadChange(tipo._id, e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="h-7 w-14 text-xs"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lista de prendas */}
          {prendas.length > 0 && (
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Prendas a Entregar</h4>
                <Badge variant="secondary">
                  {prendas.reduce((sum, p) => sum + p.cantidad, 0)} prendas
                </Badge>
              </div>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {prendas.map((prenda) => (
                  <div
                    key={prenda.id}
                    className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded border text-sm"
                  >
                    <div className="flex-1">
                      <span className="font-medium">{prenda.nombre}</span>
                      {prenda.descripcion && (
                        <span className="text-muted-foreground ml-2">({prenda.descripcion})</span>
                      )}
                      <Badge variant="outline" className="ml-2 text-xs">
                        x{prenda.cantidad}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEliminarPrenda(prenda.id)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRegistrar} 
            disabled={loading || prendas.length === 0}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading ? 'Registrando...' : 'Registrar entrega'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalEntregaRopa;
